<template>
  <b-modal
    v-model="showModal"
    title="Salesman Salary Details"
    size="xl"
    centered
    no-close-on-backdrop
    ok-only
    ok-title="Close"
    @ok="$emit('modal-closed')"
    @close="$emit('modal-closed')"
  >
    <b-alert
      variant="danger"
      :show="errorMessage ? true : false"
    >
      <div
        v-if="typeof errorMessage === 'object'"
        class="alert-body"
      >
        <p
          v-for="(message, index) in errorMessage"
          :key="index"
        >
          {{ message }}
        </p>
      </div>
      <p
        v-else
        class="alert-body"
      >
        {{ errorMessage }}
      </p>
    </b-alert>

    <b-row class="mb-2">
      <b-col md="4">
        <b-form-group
          label="Month-Year"
          label-for="selectedMonth"
        >
          <flat-pickr
            id="selectedMonth"
            v-model="selectedMonth"
            placeholder="Select Month"
            :config="flatpickrConfig"
          />
        </b-form-group>
      </b-col>
      <b-col
        md="8"
        class="text-right d-flex align-items-center justify-content-end"
      >
        <b-button
          variant="outline-primary"
          :disabled="!data || printing"
          @click="handlePrint"
        >
          <feather-icon
            v-if="!printing"
            icon="PrinterIcon"
            size="16"
          />
          <b-spinner
            v-if="printing"
            size="16"
            label="Printing"
            variant="primary"
            small
          />
          <span class="ml-1">Print</span>
        </b-button>
      </b-col>
    </b-row>

    <div
      v-if="loading"
      class="text-center"
    >
      <b-spinner
        label="Small Spinner"
        variant="primary"
      />
    </div>

    <div v-if="data && !loading">
      <!-- Summary Section -->
      <b-table-simple
        responsive
        hover
        class="compact-table"
      >
        <b-tbody>
          <b-tr>
            <b-th>Salesman Name</b-th>
            <b-td>{{ data.salesman_name }}</b-td>
            <b-th>
              Total Salary
            </b-th>
            <b-td
              class="text-right h5"
              :class="data.total_salary < 0 ? 'text-danger' : 'text-success'"
            >
              {{ formateNumber(data.total_salary) }}
            </b-td>
            <b-th>Total Buyers</b-th>
            <b-td>{{ data.total_buyers }}</b-td>
          </b-tr>
          <b-tr>
            <b-th>Total Target</b-th>
            <b-td class="text-right">
              {{ formateNumber(data.total_target) }}
            </b-td>
            <b-th>Total Sales</b-th>
            <b-td class="text-right">
              {{ formateNumber(data.total_sales) }}
            </b-td>
            <b-th>Total Returns</b-th>
            <b-td class="text-right">
              {{ formateNumber(data.total_returns) }}
            </b-td>
          </b-tr>
          <b-tr>
            <b-th>Net Sales</b-th>
            <b-td class="text-right">
              {{ formateNumber(data.total_net_sales) }}
            </b-td>
            <b-th>Achievement Difference</b-th>
            <b-td
              class="text-right"
              :class="data.achievement_difference < 0 ? 'text-danger' : 'text-success'"
            >
              {{ formateNumber(data.achievement_difference) }}
            </b-td>
            <b-th>Bonus Amount</b-th>
            <b-td class="text-right text-success">
              {{ formateNumber(data.bonus_amount) }}
            </b-td>
          </b-tr>
          <b-tr>
            <b-th>Penalty Amount</b-th>
            <b-td class="text-right text-danger">
              {{ formateNumber(data.penalty_amount) }}
            </b-td>
            <b-th>Party Handling Fees</b-th>
            <b-td class="text-right">
              {{ formateNumber(data.total_party_handling_fees) }}
            </b-td>
            <b-th>Delivery Incentive</b-th>
            <b-td class="text-right">
              {{ formateNumber(data.delivery_incentive) }}
            </b-td>
          </b-tr>
          <b-tr>
            <b-th>Penalties Adjusted Returns</b-th>
            <b-td class="text-right">
              {{ formateNumber(data.penalties_adjusted_returns) }}
            </b-td>
            <b-th>Penalties Adjusted Sales</b-th>
            <b-td class="text-right">
              {{ formateNumber(data.penalties_adjusted_total_sales) }}
            </b-td>
            <b-th />
            <b-td />
          </b-tr>
        </b-tbody>
      </b-table-simple>

      <!-- Buyers Details Section -->
      <div
        v-if="data.buyers_details && data.buyers_details.length > 0"
        class="mt-3"
      >
        <h5 class="mb-2">
          Buyers Details
        </h5>
        <b-table
          :items="data.buyers_details"
          :fields="buyersTableFields"
          responsive="sm"
          striped
          hover
          class="compact-table"
        >
          <template #cell(buyer_identifier)="row">
            {{ row.item.buyer_identifier }}
          </template>
          <template #cell(discount)="row">
            {{ row.item.discount }}%
          </template>
          <template #cell(mode_of_payment)="row">
            <b-badge :variant="row.item.mode_of_payment === 'credit' ? 'warning' : 'success'">
              {{ row.item.mode_of_payment }}
            </b-badge>
          </template>
          <template #cell(sales)="row">
            {{ formateNumber(row.item.sales) }}
          </template>
          <template #cell(returns)="row">
            {{ formateNumber(row.item.returns) }}
          </template>
          <template #cell(net_sales)="row">
            {{ formateNumber(row.item.net_sales) }}
          </template>
          <template #cell(target)="row">
            {{ formateNumber(row.item.target) }}
          </template>
          <template #cell(bonus)="row">
            <span :class="row.item.bonus < 0 ? 'text-danger' : 'text-success'">
              {{ formateNumber(row.item.bonus) }}
            </span>
          </template>
          <template #cell(party_handling_fee)="row">
            {{ formateNumber(row.item.party_handling_fee) }}
          </template>
          <template #cell(total)="row">
            <span :class="row.item.total < 0 ? 'text-danger' : 'text-success'">
              {{ formateNumber(row.item.total) }}
            </span>
          </template>
        </b-table>
      </div>

      <div
        v-else
        class="text-center text-muted mt-3"
      >
        No buyer details available for this period
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal,
  BRow,
  BCol,
  BTableSimple,
  BTr,
  BTh,
  BTd,
  BTbody,
  BSpinner,
  BAlert,
  BTable,
  BFormGroup,
  BBadge,
  BButton,
} from 'bootstrap-vue'
import dateMixin from '@/mixins/dateMixin'
import axios from 'axios'
import formatMixin from '@/mixins/formatMixin'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import MonthSelectPlugin from 'flatpickr/dist/plugins/monthSelect/index'
import 'flatpickr/dist/plugins/monthSelect/style.css'
import getParsedQueryString from '@/utils/headerQueryParser'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BModal,
    BRow,
    BCol,
    BTableSimple,
    BTr,
    BTh,
    BTd,
    BTbody,
    BSpinner,
    BAlert,
    BTable,
    BFormGroup,
    BBadge,
    BButton,
    flatPickr,
  },
  mixins: [dateMixin, formatMixin],
  props: {
    value: { type: Boolean, default: false },
    salesmanId: { type: Number, required: true },
  },
  data() {
    return {
      data: null,
      loading: false,
      errorMessage: '',
      printing: false,
      selectedMonth: this.getLastMonth(),
      flatpickrConfig: {
        dateFormat: 'Y-m',
        altInput: true,
        altFormat: 'F Y',
        plugins: [new MonthSelectPlugin({ shorthand: true, dateFormat: 'Y-m', altFormat: 'F Y' })],
      },
      buyersTableFields: [
        { key: 'buyer_identifier', label: 'Buyer', sortable: false },
        { key: 'discount', label: 'Discount', sortable: false },
        { key: 'mode_of_payment', label: 'Payment Mode', sortable: false },
        {
          key: 'sales', label: 'Sales', sortable: false, class: 'text-right',
        },
        {
          key: 'returns', label: 'Returns', sortable: false, class: 'text-right',
        },
        {
          key: 'net_sales', label: 'Net Sales', sortable: false, class: 'text-right',
        },
        {
          key: 'target', label: 'Target', sortable: false, class: 'text-right',
        },
        {
          key: 'bonus', label: 'Bonus', sortable: false, class: 'text-right',
        },
        {
          key: 'party_handling_fee', label: 'Handling Fee', sortable: false, class: 'text-right',
        },
        {
          key: 'total', label: 'Total', sortable: false, class: 'text-right',
        },
      ],
    }
  },
  computed: {
    showModal: {
      get() {
        return this.value
      },
      set(newVal) {
        this.$emit('input', newVal)
      },
    },
  },
  watch: {
    selectedMonth(newValue) {
      if (newValue) {
        this.fetchSalaryDetails()
      }
    },
  },
  mounted() {
    this.fetchSalaryDetails()
  },
  methods: {
    getLastMonth() {
      const now = new Date()
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const year = lastMonth.getFullYear()
      const month = String(lastMonth.getMonth() + 1).padStart(2, '0')
      return `${year}-${month}`
    },
    async fetchSalaryDetails() {
      try {
        this.loading = true
        this.errorMessage = ''

        const [year, month] = this.selectedMonth.split('-')
        const monthDate = `${year}-${month}-01`

        const params = {
          salesman: this.salesmanId,
          month_date: monthDate,
        }

        const { data } = await axios.get(
          `/accounts/salesman-salaries/?${getParsedQueryString(params)}`,
        )

        const [firstResult] = data.results || []

        if (firstResult && data.settings_used) {
          // Process buyers_details with bonus and total calculations
          if (firstResult.buyers_details) {
            const settings = data.settings_used
            const penaltyRatioForReturns = settings.penalty_ratio_for_returns || 1.2
            const rewardRatioForOverAchievement = settings.reward_ratio_for_over_achievement || 0.01
            const penaltyRatioForUnderAchievement = settings.penalty_ratio_for_under_achievement || 0.01

            firstResult.buyers_details = firstResult.buyers_details.map(buyer => {
              const sales = buyer.sales || 0
              const returns = buyer.returns || 0
              const target = buyer.target || 0
              const partyHandlingFee = buyer.party_handling_fee || 0

              // Calculate net_sales_returnincluded
              const netSalesReturnIncluded = sales - (returns * penaltyRatioForReturns)

              // Calculate bonus
              let bonus = 0
              if (netSalesReturnIncluded > target) {
                // Over achievement - bonus
                bonus = (netSalesReturnIncluded - target) * rewardRatioForOverAchievement
              } else if (netSalesReturnIncluded < target) {
                // Under achievement - penalty (negative bonus)
                bonus = (netSalesReturnIncluded - target) * penaltyRatioForUnderAchievement
              }

              // Calculate total
              const total = bonus + partyHandlingFee

              return {
                ...buyer,
                bonus: parseFloat(bonus.toFixed(2)),
                total: parseFloat(total.toFixed(2)),
              }
            })
          }

          this.data = firstResult
        } else {
          this.data = null
          this.errorMessage = 'No salary data found for the selected month'
        }
      } catch (error) {
        this.errorMessage = error?.response?.data?.error || 'Failed to fetch salary details!'
      } finally {
        this.loading = false
      }
    },

    async handlePrint() {
      try {
        this.printing = true
        await this.printSalaryReport()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error.message || 'Failed to print!',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.printing = false
      }
    },

    printSalaryReport() {
      return new Promise((resolve, reject) => {
        try {
          if (!this.data) {
            reject(new Error('No data available to print'))
            return
          }

          const [year, month] = this.selectedMonth.split('-')
          const monthYear = new Date(year, month - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })

          // Build summary section
          const summaryHTML = `
  <div style="margin-bottom: 15px; padding: 10px; background-color: #f8f9fa; border: 1px solid #dee2e6; border-radius: 4px;">
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
      <h2 style="margin: 0; font-size: 18px;">Salesman Salary Details</h2>
      <div style="font-size: 12px; font-weight: bold;">${monthYear}</div>
    </div>
    <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 10px;">
      <div style="font-size: 11px;"><strong>Salesman:</strong> ${this.data.salesman_name}</div>
      <div style="font-size: 11px;"><strong>Total Buyers:</strong> ${this.data.total_buyers}</div>
      <div style="font-size: 11px;"><strong>Total Salary:</strong> <span style="color: ${this.data.total_salary < 0 ? '#dc3545' : '#28a745'}; font-weight: bold;">${this.formateNumber(this.data.total_salary)}</span></div>
    </div>
    <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 10px;">
      <div style="font-size: 11px;"><strong>Total Target:</strong> ${this.formateNumber(this.data.total_target)}</div>
      <div style="font-size: 11px;"><strong>Total Sales:</strong> ${this.formateNumber(this.data.total_sales)}</div>
      <div style="font-size: 11px;"><strong>Total Returns:</strong> ${this.formateNumber(this.data.total_returns)}</div>
    </div>
    <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 10px;">
      <div style="font-size: 11px;"><strong>Net Sales:</strong> ${this.formateNumber(this.data.total_net_sales)}</div>
      <div style="font-size: 11px;"><strong>Achievement Diff:</strong> <span style="color: ${this.data.achievement_difference < 0 ? '#dc3545' : '#28a745'};">${this.formateNumber(this.data.achievement_difference)}</span></div>
      <div style="font-size: 11px;"><strong>Bonus Amount:</strong> <span style="color: #28a745;">${this.formateNumber(this.data.bonus_amount)}</span></div>
    </div>
    <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px;">
      <div style="font-size: 11px;"><strong>Penalty Amount:</strong> <span style="color: #dc3545;">${this.formateNumber(this.data.penalty_amount)}</span></div>
      <div style="font-size: 11px;"><strong>Party Handling Fees:</strong> ${this.formateNumber(this.data.total_party_handling_fees)}</div>
      <div style="font-size: 11px;"><strong>Delivery Incentive:</strong> ${this.formateNumber(this.data.delivery_incentive)}</div>
    </div>
  </div>
`

          // Build buyers table if exists
          let buyersTableHTML = ''
          if (this.data.buyers_details && this.data.buyers_details.length > 0) {
            buyersTableHTML = '<h3 style="margin-top: 15px; margin-bottom: 8px; font-size: 14px;">Buyers Details</h3>'
            buyersTableHTML += '<table style="width: 100%; border-collapse: collapse; margin-bottom: 20px; table-layout: auto;">'

            // Add header row
            buyersTableHTML += '<thead><tr>'
            buyersTableHTML += '<th style="background-color: #e8e8e8 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 11px; border: 1px solid #000; padding: 3px; text-align: left;">Buyer</th>'
            buyersTableHTML += '<th style="background-color: #e8e8e8 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 11px; border: 1px solid #000; padding: 3px; text-align: center;">Disc%</th>'
            buyersTableHTML += '<th style="background-color: #e8e8e8 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 11px; border: 1px solid #000; padding: 3px; text-align: center;">Payment</th>'
            buyersTableHTML += '<th style="background-color: #e8e8e8 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 11px; border: 1px solid #000; padding: 3px; text-align: right;">Sales</th>'
            buyersTableHTML += '<th style="background-color: #e8e8e8 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 11px; border: 1px solid #000; padding: 3px; text-align: right;">Returns</th>'
            buyersTableHTML += '<th style="background-color: #e8e8e8 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 11px; border: 1px solid #000; padding: 3px; text-align: right;">Net Sales</th>'
            buyersTableHTML += '<th style="background-color: #e8e8e8 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 11px; border: 1px solid #000; padding: 3px; text-align: right;">Target</th>'
            buyersTableHTML += '<th style="background-color: #e8e8e8 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 11px; border: 1px solid #000; padding: 3px; text-align: right;">Bonus</th>'
            buyersTableHTML += '<th style="background-color: #e8e8e8 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 11px; border: 1px solid #000; padding: 3px; text-align: right;">Handling</th>'
            buyersTableHTML += '<th style="background-color: #e8e8e8 !important; -webkit-print-color-adjust: exact; color-adjust: exact; color: black !important; font-weight: bold; font-size: 11px; border: 1px solid #000; padding: 3px; text-align: right;">Total</th>'
            buyersTableHTML += '</tr></thead>'

            // Add data rows
            buyersTableHTML += '<tbody>'
            this.data.buyers_details.forEach((buyer, index) => {
              const bgColor = index % 2 === 0 ? '#ffffff' : '#f8f8f8'
              const bonusColor = buyer.bonus < 0 ? '#dc3545' : '#28a745'
              const totalColor = buyer.total < 0 ? '#dc3545' : '#28a745'

              buyersTableHTML += '<tr>'
              buyersTableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 10px; border: 1px solid #000; padding: 3px; text-align: left; word-wrap: break-word;">${buyer.buyer_identifier || '-'}</td>`
              buyersTableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 10px; border: 1px solid #000; padding: 3px; text-align: center;">${buyer.discount}%</td>`
              buyersTableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 10px; border: 1px solid #000; padding: 3px; text-align: center;">${buyer.mode_of_payment}</td>`
              buyersTableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 10px; border: 1px solid #000; padding: 3px; text-align: right;">${this.formateNumber(buyer.sales)}</td>`
              buyersTableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 10px; border: 1px solid #000; padding: 3px; text-align: right;">${this.formateNumber(buyer.returns)}</td>`
              buyersTableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 10px; border: 1px solid #000; padding: 3px; text-align: right;">${this.formateNumber(buyer.net_sales)}</td>`
              buyersTableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 10px; border: 1px solid #000; padding: 3px; text-align: right;">${this.formateNumber(buyer.target)}</td>`
              buyersTableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 10px; border: 1px solid #000; padding: 3px; text-align: right; color: ${bonusColor};">${this.formateNumber(buyer.bonus)}</td>`
              buyersTableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 10px; border: 1px solid #000; padding: 3px; text-align: right;">${this.formateNumber(buyer.party_handling_fee)}</td>`
              buyersTableHTML += `<td style="background-color: ${bgColor} !important; -webkit-print-color-adjust: exact; color-adjust: exact; font-size: 10px; border: 1px solid #000; padding: 3px; text-align: right; color: ${totalColor};">${this.formateNumber(buyer.total)}</td>`
              buyersTableHTML += '</tr>'
            })
            buyersTableHTML += '</tbody></table>'
          }

          // Create iframe for printing
          const iframe = document.createElement('iframe')
          iframe.style.position = 'absolute'
          iframe.style.top = '-9999px'
          iframe.style.left = '-9999px'
          document.body.appendChild(iframe)

          // Write content to iframe
          iframe.contentDocument.open()
          iframe.contentDocument.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <style>
            @media print {
              * {
                -webkit-print-color-adjust: exact !important;
                color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              
              body {
                margin: 5px;
                font-family: Arial, sans-serif;
              }
              
              table {
                page-break-inside: auto;
              }
              
              tr {
                page-break-inside: avoid;
                page-break-after: auto;
              }
              
              th, td {
                page-break-inside: avoid;
              }
            }
            
            @page {
              margin: 0.3in 0.2in;
              size: A4 portrait;
            }
            
            body {
              font-family: Arial, sans-serif;
              margin: 5px;
            }
          </style>
        </head>
        <body>
          ${summaryHTML}
          ${buyersTableHTML}
        </body>
        </html>
      `)
          iframe.contentDocument.close()

          setTimeout(() => {
            iframe.contentWindow.print()

            iframe.contentWindow.addEventListener('afterprint', () => {
              if (document.body.contains(iframe)) {
                document.body.removeChild(iframe)
              }
              resolve('Print completed successfully')
            })

            // Fallback cleanup
            setTimeout(() => {
              if (document.body.contains(iframe)) {
                document.body.removeChild(iframe)
                resolve('Print completed')
              }
            }, 500)
          }, 500)
        } catch (error) {
          reject(error)
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.compact-table {
  th {
    width: 16.66%;
  }
  td {
    width: 16.66%;
  }
}
</style>
